import { componentTypeLabel } from '@netfoundry-ui/feature/form/latency-policy-form';
import { AlertSeverity, ComponentType, OfflinePolicy, OfflineRole, OfflineRungs } from '@netfoundry-ui/shared/model';
import { formatIsoDuration } from '@netfoundry-ui/util/duration';
import { isEmpty, isNil } from 'lodash';

/** One rung of an offline ladder: its key in {@link OfflineRungs}, its severity and its label. */
export interface OfflineRung {
    key: keyof OfflineRungs;
    severity: AlertSeverity;
    label: string;
}

/** The rungs least severe first, which is the order a component passes them while it stays offline. */
export const OFFLINE_RUNGS: OfflineRung[] = [
    { key: 'info', severity: 'INFO', label: 'Info' },
    { key: 'minor', severity: 'MINOR', label: 'Minor' },
    { key: 'major', severity: 'MAJOR', label: 'Major' },
    { key: 'critical', severity: 'CRITICAL', label: 'Critical' },
];

/** The text shown for a rung nobody set. */
export const UNSET_RUNG = '—';

/** Whether a ladder has at least one rung set. A ladder with none inherits the next level's ladder. */
export function hasRungs(ladder?: OfflineRungs | null): boolean {
    return OFFLINE_RUNGS.some((rung) => !isEmpty((ladder?.[rung.key] || '').trim()));
}

/** One rung in words, `PT4H` as `4 h`. A value that is not a duration is shown as sent, and an unset rung as an em dash. */
export function rungText(value?: string | null): string {
    if (isEmpty((value || '').trim())) {
        return UNSET_RUNG;
    }
    return formatIsoDuration(value) ?? `${value}`;
}

/** The set rungs in one line, `Major 4 h · Critical 1 d`, or `''` when no rung is set. */
export function ladderSummary(ladder?: OfflineRungs | null): string {
    return OFFLINE_RUNGS.filter((rung) => !isEmpty((ladder?.[rung.key] || '').trim()))
        .map((rung) => `${rung.label} ${rungText(ladder?.[rung.key])}`)
        .join(' · ');
}

/** The placeholder for each rung box: what applies when the box is left empty. */
export function rungPlaceholders(inherited?: OfflineRungs | null): Record<keyof OfflineRungs, string> {
    const placeholders = {} as Record<keyof OfflineRungs, string>;
    for (const rung of OFFLINE_RUNGS) {
        const value = inherited?.[rung.key];
        placeholders[rung.key] = isEmpty((value || '').trim()) ? 'Not set' : rungText(value);
    }
    return placeholders;
}

/**
 * The ladder a level inherits: the first one below it that has rungs.
 * Pass the levels narrowest first, for example the network default and then the fleet ladder.
 */
export function inheritedLadder(...levels: (OfflineRungs | null | undefined)[]): OfflineRungs | undefined {
    return levels.find((ladder) => hasRungs(ladder)) || undefined;
}

/** The roles an identity is graded per, with what each one means. */
export const OFFLINE_ROLES: { value: OfflineRole; label: string; detail: string }[] = [
    { value: 'HOSTING', label: 'Hosting', detail: 'It hosted a service in the day before it went offline.' },
    { value: 'DIALING', label: 'Dialing', detail: 'It did not host a service in the day before it went offline.' },
];

export function offlineRoleLabel(role?: OfflineRole | null): string {
    return OFFLINE_ROLES.find((entry) => entry.value === role)?.label || (role ? `${role}` : '');
}

/** What a policy for each component type covers, under its button on the create form. */
const OFFLINE_TYPE_DETAILS: Partial<Record<ComponentType, string>> = {
    EDGE_ROUTER: 'One edge router. Alerts when a router that has been in service goes offline.',
    IDENTITY: 'One identity, as hosting or dialing. Identities raise no offline alerts until a policy switches them on.',
};

/** The sentence under a component type's button, or a plain one for a type this console has no words for. */
export function offlineTypeDetail(componentType?: ComponentType | null): string {
    if (isNil(componentType)) {
        return '';
    }
    return OFFLINE_TYPE_DETAILS[componentType] || `One ${(componentTypeLabel(componentType) || 'component').toLowerCase()}.`;
}

/** The kind in words: `Edge router`, or `Hosting identity` for a type graded per role. */
export function offlineKindLabel(componentType?: ComponentType | null, role?: OfflineRole | null): string {
    const type = componentTypeLabel(componentType);
    return isNil(role) || isEmpty(type) ? type : `${offlineRoleLabel(role)} ${type.toLowerCase()}`;
}

/** `Network policy` for a default, else `Edge router override` or `Hosting identity override`. */
export function offlinePolicyScope(policy: OfflinePolicy): string {
    if (policy.networkDefault || isEmpty(policy.componentId)) {
        return 'Network policy';
    }
    const kind = offlineKindLabel(policy.componentType, policy.role);
    return isEmpty(kind) ? 'Component override' : `${kind} override`;
}

export function offlineStatusLabel(policy?: Pick<OfflinePolicy, 'enabled'> | null): string {
    return policy?.enabled ? 'On' : 'Off';
}

/** The ladder column of a listing: off, the set rungs, or a note that the policy inherits. */
export function offlineLadderText(policy: OfflinePolicy): string {
    if (!policy.enabled) {
        return 'No alerts';
    }
    const summary = ladderSummary(policy.ladder);
    return isEmpty(summary) ? 'Uses the network ladder' : summary;
}

/** The component ids of an override. Read from `componentIds`, and from the opaque key only when the parts are absent. */
export function offlineComponentIds(policy?: OfflinePolicy | null): string[] {
    const parts = policy?.componentIds || [];
    if (parts.length > 0) {
        return parts;
    }
    return isNil(policy?.componentId) || isEmpty(policy?.componentId) ? [] : [policy?.componentId as string];
}
